import { axios } from '@/services/axios';

// Shared cache for CompanyNavigationList data - stores PROCESSED data
const navigationCache = {};
// Index by ID for fast lookup
const navigationIndexCache = {};
// In-flight request tracking to prevent duplicate API calls
const inFlightRequests = {};
// Bumped on clear so a request started before it can't repopulate the cache
let cacheGeneration = 0;

/**
 * Get cached PROCESSED data synchronously (instant, no async)
 * Returns { partners: [], companiesByPartner: {} } or null if not cached
 * @param {string} wholesalerId - The wholesaler ID
 * @param {number} scope - User scope (40 = partner, 60+ = wholesaler/global)
 * @param {string} partnerId - The partner ID (required for scope 40)
 */
export const getCachedNavigationData = (
    wholesalerId,
    scope = 60,
    partnerId = null,
) => {
    const cacheKey =
        scope === 40 && partnerId
            ? `partner_${partnerId}`
            : `wholesaler_${wholesalerId}`;
    return navigationCache[cacheKey] || null;
};

/**
 * Get a specific item by ID from cache (fast lookup)
 * @param {string} wholesalerId - The wholesaler ID
 * @param {string} itemId - The item ID to find
 * @param {number} scope - User scope (40 = partner, 60+ = wholesaler/global)
 * @param {string} partnerId - The partner ID (required for scope 40)
 * @returns {Object|null} - The item or null if not found
 */
export const getCachedItemById = (
    wholesalerId,
    itemId,
    scope = 60,
    partnerId = null,
) => {
    const cacheKey =
        scope === 40 && partnerId
            ? `partner_${partnerId}`
            : `wholesaler_${wholesalerId}`;
    const index = navigationIndexCache[cacheKey];
    return index ? index[itemId] : null;
};

/**
 * Fetch and cache CompanyNavigationList data
 * Processes and stores ready-to-use data structure
 * @param {string} wholesalerId - The wholesaler ID
 * @param {number} scope - User scope (40 = partner, 60+ = wholesaler/global)
 * @param {string} partnerId - The partner ID (required for scope 40)
 * @returns {Promise<Object>} - { partners: [], companiesByPartner: {} }
 */
export const getNavigationData = async (
    wholesalerId,
    scope = 60,
    partnerId = null,
) => {
    // Determine cache key based on scope
    const cacheKey =
        scope === 40 && partnerId
            ? `partner_${partnerId}`
            : `wholesaler_${wholesalerId}`;

    if (!wholesalerId) {
        return { partners: [], companiesByPartner: {} };
    }
    if (scope === 40 && !partnerId) {
        return { partners: [], companiesByPartner: {} };
    }

    // Return cached PROCESSED data if available
    if (navigationCache[cacheKey]) {
        return navigationCache[cacheKey];
    }

    // If a request is already in flight for this cache key, wait for it
    if (inFlightRequests[cacheKey]) {
        return inFlightRequests[cacheKey];
    }

    const generation = cacheGeneration;

    // Mark this request as in-flight
    const requestPromise = (async () => {
        try {
            // Build URL based on scope
            let url;
            let headerScope;
            if (scope === 40 && partnerId) {
                // Partner Admin: use both wholesalerID and partnerID, scope 40 in headers
                url = `/companies/List?wholesalerID=${wholesalerId}&partnerID=${partnerId}`;
                headerScope = 40;
            } else {
                // Wholesaler/Global Admin (scope 60+): use wholesalerID only, always scope 60 in headers
                url = `/companies/List?wholesalerID=${wholesalerId}`;
                headerScope = 60;
            }

            const res = await axios.get(url, {
                _requestScopeOverride: headerScope,
            });
            let rawData = res.data || [];

            // Process data immediately - build partners and companiesByPartner
            const partnersMap = new Map();
            const groupedByPartner = {};
            const index = {};

            rawData.forEach((item) => {
                const isPartner = item.id === item.partnerID;

                if (isPartner) {
                    partnersMap.set(item.id, {
                        id: item.id,
                        friendlyName: item.friendlyName || item.name,
                        legalName: item.legalName || item.name,
                        name: item.name,
                        attribute1: item.attribute1,
                        attribute2: item.attribute2,
                        attribute3: item.attribute3,
                    });
                }

                const company = {
                    id: item.id,
                    friendlyName: item.friendlyName || item.name,
                    legalName: item.legalName || item.entityName || item.name,
                    entityName: item.entityName,
                    name: item.name,
                    partnerID: item.partnerID,
                    partnerName: item.partnerName,
                    status: item.status,
                    isTrial: item.isTrial,
                    isActive: item.isActive,
                    approved: item.approved,
                    trialHold: item.trialHold,
                    trialEnd: item.trialEnd,
                    tenantID: item.msTenantID,
                    initialMicrosoftDomain: item.initialDomain,
                    defaultPlan: item.defaultPlan,
                    serviceCountries: item.serviceCountries,
                    lastSuccessfulPair: item.lastSuccessfulPair,
                    attribute1: item.attribute1,
                    attribute2: item.attribute2,
                    attribute3: item.attribute3,
                    attributes: item.attributes,
                };

                const partnerId = item.partnerID || 'unassigned';
                if (!groupedByPartner[partnerId]) {
                    groupedByPartner[partnerId] = [];
                }
                groupedByPartner[partnerId].push(company);

                // Build index
                if (item.id) {
                    index[item.id] = item;
                }
            });

            // Pre-sort partners alphabetically - do it ONCE, never again
            const sortedPartners = Array.from(partnersMap.values()).sort(
                (a, b) =>
                    (a.friendlyName || '').localeCompare(b.friendlyName || ''),
            );

            // Pre-sort companies within each partner - do it ONCE too!
            Object.keys(groupedByPartner).forEach((partnerId) => {
                groupedByPartner[partnerId].sort((a, b) => {
                    // Partner itself first (when company.id === partnerId)
                    if (a.id === partnerId) return -1;
                    if (b.id === partnerId) return 1;
                    // Then alphabetically
                    return (a.friendlyName || '').localeCompare(
                        b.friendlyName || '',
                    );
                });
            });

            const processedData = {
                partners: sortedPartners, // Already sorted!
                companiesByPartner: groupedByPartner, // Companies already sorted too!
            };

            // Store PROCESSED data in cache (unless cleared mid-request)
            if (generation === cacheGeneration) {
                navigationCache[cacheKey] = processedData;
                navigationIndexCache[cacheKey] = index;
            }

            return processedData;
        } catch (error) {
            console.error('[NavigationCache] Error fetching data:', error);
            return { partners: [], companiesByPartner: {} };
        } finally {
            // Clean up in-flight tracking
            if (generation === cacheGeneration) {
                delete inFlightRequests[cacheKey];
            }
        }
    })();

    // Store the promise IMMEDIATELY so other callers can wait for it
    // (This happens synchronously before any async operations)
    inFlightRequests[cacheKey] = requestPromise;

    return requestPromise;
};

/**
 * Clear cache for a specific wholesaler or all cache
 * @param {string|null} wholesalerId - The wholesaler ID to clear, or null to clear all
 */
export const clearNavigationCache = (wholesalerId = null) => {
    if (wholesalerId) {
        delete navigationCache[wholesalerId];
        delete navigationIndexCache[wholesalerId];
    } else {
        cacheGeneration += 1;
        Object.keys(inFlightRequests).forEach(
            (key) => delete inFlightRequests[key],
        );
        Object.keys(navigationCache).forEach(
            (key) => delete navigationCache[key],
        );
        Object.keys(navigationIndexCache).forEach(
            (key) => delete navigationIndexCache[key],
        );
    }
};

/**
 * Refresh every "currently managing" company/partner list: the picker's
 * module cache and the navbar's react-query list. Call after a company or
 * partner is created, deleted or approved.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const refreshNavigationLists = (queryClient) => {
    clearNavigationCache();
    queryClient.invalidateQueries({ queryKey: ['companyNavigationList'] });
};

/**
 * Pre-fetch data to have it ready in cache
 * @param {string} wholesalerId - The wholesaler ID
 * @param {number} scope - User scope (40 = partner, 60+ = wholesaler/global)
 * @param {string} partnerId - The partner ID (required for scope 40)
 * @returns {Promise<Object>} - Promise that resolves when data is fetched
 */
export const prefetchNavigationData = (
    wholesalerId,
    scope = 60,
    partnerId = null,
) => {
    const cacheKey =
        scope === 40 && partnerId
            ? `partner_${partnerId}`
            : `wholesaler_${wholesalerId}`;

    if (!wholesalerId && scope !== 40) {
        return Promise.resolve({ partners: [], companiesByPartner: {} });
    }
    if (scope === 40 && !partnerId) {
        return Promise.resolve({ partners: [], companiesByPartner: {} });
    }

    if (navigationCache[cacheKey]) {
        return Promise.resolve(navigationCache[cacheKey]); // Already cached
    }

    // Fetch in background and return promise
    return getNavigationData(wholesalerId, scope, partnerId).catch((err) => {
        console.error('[NavigationCache] Prefetch error:', err);
        return { partners: [], companiesByPartner: {} };
    });
};
